import { useEffect } from "react";
import { useLocation } from "wouter";
import { assets, siteConfig } from "@/data/site";
import { getCoreHeadForPath, type HeadMeta } from "@/ssr/coreHead";

function upsertMeta(selector: string, attributes: Record<string, string>, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }
  element.content = content;
}

function applyHead(head: HeadMeta) {
  const canonical = head.canonicalPath ? `${siteConfig.url}${head.canonicalPath}` : "";
  const image = head.ogImage?.startsWith("/") ? `${siteConfig.url}${head.ogImage}` : head.ogImage || `${siteConfig.url}${assets.socialCard}`;
  document.title = head.title;
  upsertMeta('meta[name="description"]', { name: "description" }, head.description);
  upsertMeta('meta[property="og:title"]', { property: "og:title" }, head.title);
  upsertMeta('meta[property="og:description"]', { property: "og:description" }, head.description);
  upsertMeta('meta[property="og:type"]', { property: "og:type" }, head.ogType || "website");
  upsertMeta('meta[property="og:site_name"]', { property: "og:site_name" }, siteConfig.name);
  upsertMeta('meta[property="og:locale"]', { property: "og:locale" }, "en_US");
  upsertMeta('meta[property="og:image"]', { property: "og:image" }, image);
  upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url" }, image);
  if (head.ogImageWidth) upsertMeta('meta[property="og:image:width"]', { property: "og:image:width" }, String(head.ogImageWidth));
  if (head.ogImageHeight) upsertMeta('meta[property="og:image:height"]', { property: "og:image:height" }, String(head.ogImageHeight));
  if (head.ogImageAlt) upsertMeta('meta[property="og:image:alt"]', { property: "og:image:alt" }, head.ogImageAlt);
  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, image ? "summary_large_image" : "summary");
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, head.title);
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, head.description);
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, image);
  if (head.ogImageAlt) upsertMeta('meta[name="twitter:image:alt"]', { name: "twitter:image:alt" }, head.ogImageAlt);
  upsertMeta('meta[name="robots"]', { name: "robots" }, head.notFound || head.noindex ? "noindex, follow" : "index, follow");

  let canonicalElement = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (canonical) {
    if (!canonicalElement) {
      canonicalElement = document.createElement("link");
      canonicalElement.rel = "canonical";
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.href = canonical;
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonical);
  } else {
    canonicalElement?.remove();
    document.head.querySelector('meta[property="og:url"]')?.remove();
  }
}

function isImportedCatalogPath(location: string) {
  return /^\/hardware\/[^/?]+(?:\/[^/?]+)?\/?(?:\?.*)?$/i.test(location);
}

export function Head() {
  const [location] = useLocation();

  useEffect(() => {
    let cancelled = false;
    const fallback = getCoreHeadForPath(location);
    if (!isImportedCatalogPath(location)) {
      applyHead(fallback);
      return () => { cancelled = true; };
    }

    void import("@/ssr/catalogHead").then(({ getCatalogHeadForPath }) => {
      if (cancelled) return;
      const catalogHead = getCatalogHeadForPath(location.split("?")[0] || location);
      applyHead(catalogHead ?? fallback);
    }).catch(() => {
      if (!cancelled) applyHead(fallback);
    });

    return () => { cancelled = true; };
  }, [location]);

  return null;
}
